import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Loader, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { gbp, ukDate, todayISO } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const CATS = ["Fuel", "MOT", "Insurance", "Road Tax", "Servicing", "Repairs", "Tyres", "Other"];

export default function Expenses() {
  const { data: rows, loading, refetch } = useFetch("/expenses");
  const { data: vans } = useFetch("/vans");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ van_reg: "", category: "Repairs", amount: "", date: todayISO(), notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const save = async () => {
    try {
      await api.post("/expenses", form);
      toast.success("Expense recorded");
      setOpen(false); setForm({ van_reg: "", category: "Repairs", amount: "", date: todayISO(), notes: "" }); refetch();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/expenses/${id}`); refetch(); } catch { toast.error("Error"); } };

  if (loading) return <Loader />;
  const total = (rows || []).reduce((a, r) => a + r.amount, 0);

  return (
    <div className="fp-fade-in">
      <PageHeader title="Vehicle Costs" subtitle="Fuel, MOT, insurance, tax, servicing, repairs, tyres & more per van" testId="expenses-page">
        <Button onClick={() => setOpen(true)} className="w-full sm:w-auto" data-testid="add-expense-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> Add Cost</Button>
      </PageHeader>
      <Card className="p-5 mb-6 inline-flex flex-col">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Recorded Costs</p>
        <p className="font-mono text-2xl font-bold mt-1 text-rose-400">{gbp(total)}</p>
      </Card>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Van</TableHead><TableHead>Category</TableHead>
              <TableHead>Amount</TableHead><TableHead>Notes</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(rows || []).map((r) => (
                <TableRow key={r.id} data-testid={`expense-row-${r.id}`}>
                  <TableCell className="font-mono text-sm">{ukDate(r.date)}</TableCell>
                  <TableCell>{r.van_reg ? <Plate reg={r.van_reg} /> : "—"}</TableCell>
                  <TableCell>{r.category}</TableCell>
                  <TableCell className="font-mono font-semibold text-rose-400">{gbp(r.amount)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.notes || "—"}</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => remove(r.id)}><Icons.Trash2 className="h-4 w-4 text-rose-400" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {(!rows || rows.length === 0) && <EmptyState icon="Wrench" title="No costs recorded" />}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader><DialogTitle>Record Vehicle Cost</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Van</Label>
              <Select value={form.van_reg} onValueChange={set("van_reg")}>
                <SelectTrigger className="mt-1.5" data-testid="expense-van-select"><SelectValue placeholder="Select van" /></SelectTrigger>
                <SelectContent>{(vans || []).map((v) => <SelectItem key={v.id} value={v.registration}>{v.registration}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger className="mt-1.5" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount (£)</Label><Input type="number" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="expense-amount-input" /></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={set("notes")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-expense-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
